/** Where a sign-in lands when nothing (or nothing safe) asked for another page. */
export const DEFAULT_LOGIN_REDIRECT = "/";

const SSO_REDIRECT_STORAGE_KEY = "tapioka.ssoLoginRedirect";

// Any origin works: it only lets `URL` resolve a relative path, so a value that
// resolves elsewhere (`//evil.com`, `/\evil.com`, a tab inside the slashes) shows up
// as a different origin.
const PARSE_BASE = "https://tapioka.invalid";

/**
 * The in-app path a sign-in may send the user back to. `?redirect=` comes from the
 * address bar, so anything that is not a same-origin path — or that points back at
 * Login — falls back to the default instead of becoming an open redirect.
 */
export function safeRedirect(raw: unknown): string {
  if (typeof raw !== "string" || !raw.startsWith("/")) return DEFAULT_LOGIN_REDIRECT;

  let url: URL;
  try {
    url = new URL(raw, PARSE_BASE);
  } catch {
    return DEFAULT_LOGIN_REDIRECT;
  }
  if (url.origin !== PARSE_BASE || url.pathname.replace(/\/$/, "") === "/login") return DEFAULT_LOGIN_REDIRECT;

  return `${url.pathname}${url.search}${url.hash}`;
}

/** Login's query for a user who should come back to `target` after signing in. */
export function loginQuery(target: string): { redirect?: string } {
  const redirect = safeRedirect(target);
  return redirect === DEFAULT_LOGIN_REDIRECT ? {} : { redirect };
}

/**
 * The SSO round-trip leaves the app for the identity provider, and the backend's
 * start URL takes no return target, so the target waits in sessionStorage.
 * Storage can be unavailable (private mode, blocked site data): then SSO lands on
 * the default page, as it did before.
 */
export function rememberSsoRedirect(raw: unknown): void {
  try {
    const redirect = safeRedirect(raw);
    if (redirect === DEFAULT_LOGIN_REDIRECT) sessionStorage.removeItem(SSO_REDIRECT_STORAGE_KEY);
    else sessionStorage.setItem(SSO_REDIRECT_STORAGE_KEY, redirect);
  } catch {
    // Storage unavailable; see above.
  }
}

/** Reads and clears the target stored by {@link rememberSsoRedirect}. */
export function takeSsoRedirect(): string {
  try {
    const stored = sessionStorage.getItem(SSO_REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(SSO_REDIRECT_STORAGE_KEY);
    return safeRedirect(stored);
  } catch {
    return DEFAULT_LOGIN_REDIRECT;
  }
}
